package com.evoapps.evoshift.domain.model

/**
 * SH-173: who gets the availability request by e-mail as well, with a personal link to a web form
 * (no app, no sign-in). Stored on the request as `emailLinks`; functions/availabilityLinks.js
 * issues the links when the request is created.
 */
enum class AvailabilityEmailLinks(val stored: String?) {
    /** In the app only (inbox and push). */
    NONE(null),

    /**
     * Also by e-mail to people without the app: they chose e-mail, or no device of theirs is
     * registered for notifications (people the manager added, who never installed the app).
     */
    WITHOUT_APP("NO_APP"),

    /** Also by e-mail to everyone active at the workplace who has an address. */
    EVERYONE("ALL");

    companion object {
        fun fromStored(value: String?): AvailabilityEmailLinks = entries.firstOrNull { it.stored != null && it.stored == value } ?: NONE
    }
}

/**
 * SH-173: what the web form behind an e-mailed link shows: the person's first name, the
 * workplace, the month and the days asked for, the workplace's slots and the answers so far.
 * Nothing else of the person or the workplace is readable through the link.
 */
data class AvailabilityLinkForm(
    val firstName: String,
    val workplaceName: String,
    /** "YYYY-MM". */
    val month: String,
    /** The days asked for; null for the whole month. */
    val period: AvailabilityPeriod?,
    /** ISO 8601, null when unknown. */
    val dueDate: String?,
    /** ISO 8601: when the link stops working. */
    val expiresAt: String,
    /** The manager's message with the request. */
    val message: String?,
    /** 1 = Monday … 7 = Sunday. */
    val firstDayOfWeek: Int,
    val slots: List<AvailabilitySlot>,
    /** The workplace's copy of the month (empty: nothing given yet). */
    val days: Map<Int, DayPreference>,
    /** The month was sent already (sending again changes it). */
    val submitted: Boolean,
    /** The managers locked the month: shown, not changeable. */
    val locked: Boolean
)

/** SH-173: why an availability link's form cannot be shown or sent. */
class AvailabilityLinkException(val reason: Reason) : Exception("Availability link: $reason") {
    enum class Reason {
        /** Unknown, expired, revoked or replaced link (one answer for all of them). */
        INVALID,
        /** Too many attempts in a short time. */
        RATE_LIMITED,
        /** The managers locked the month. */
        LOCKED,
        /** The workplace's plan ended: read only. */
        READ_ONLY,
        /** The days sent were refused (should not happen with the form). */
        INVALID_DAYS
    }
}
